import { instantScrollBehaviorOnFocusKey } from './types';

let lastFocusEventTime = 0;
let lastScrollTime = 0;
let scrollTimeoutId: NodeJS.Timeout | null = null;
let animationFrameId: number | null = null;

const RAPID_EVENT_THRESHOLD = 300;
const SCROLL_DURATION_RAPID = 150;
const SCROLL_DURATION_DEFAULT = 300;

const easeInOutQuad = (t: number): number => {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
};

const getDuration = (isRapidEvent: boolean): number => {
  if (isRapidEvent) return SCROLL_DURATION_RAPID;
  return SCROLL_DURATION_DEFAULT;
};

const animateScroll = (targetY: number, duration: number) => {
  const startY = window.scrollY;
  const distance = targetY - startY;
  const startTime = Date.now();

  if (animationFrameId !== null) {
    cancelAnimationFrame(animationFrameId);
  }

  const animate = () => {
    const elapsed = Date.now() - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easeInOutQuad(progress);
    const currentY = startY + distance * easedProgress;

    window.scrollTo(0, currentY);

    if (progress < 1) {
      animationFrameId = requestAnimationFrame(animate);
    } else {
      animationFrameId = null;
    }
  };

  animationFrameId = requestAnimationFrame(animate);
};

const animatedScroll = (top: number) => {
  const now = Date.now();
  const timeSinceLastEvent = now - lastFocusEventTime;
  const timeSinceLastScroll = now - lastScrollTime;

  lastFocusEventTime = now;

  const isPageNavigation = sessionStorage.getItem(instantScrollBehaviorOnFocusKey) === 'true';
  const isRapidEvent = timeSinceLastEvent < RAPID_EVENT_THRESHOLD;

  // Page navigation requires truly instant scroll without animation
  if (isPageNavigation) {
    if (scrollTimeoutId) {
      clearTimeout(scrollTimeoutId);
      scrollTimeoutId = null;
    }
    if (animationFrameId !== null) {
      cancelAnimationFrame(animationFrameId);
      animationFrameId = null;
    }
    lastScrollTime = Date.now();
    window.scrollTo(0, top);
    sessionStorage.setItem(instantScrollBehaviorOnFocusKey, 'false');
    return;
  }

  const executeScroll = () => {
    lastScrollTime = Date.now();
    const duration = getDuration(isRapidEvent);
    animateScroll(top, duration);
    scrollTimeoutId = null;
  };

  if (scrollTimeoutId) {
    clearTimeout(scrollTimeoutId);
    scrollTimeoutId = null;
  }

  // Execute immediately if rapid event or enough time has passed since last scroll
  if (isRapidEvent || timeSinceLastScroll >= RAPID_EVENT_THRESHOLD) {
    executeScroll();
  } else {
    // Throttle scroll: wait for minimum interval to pass before executing
    const delayBeforeScroll = RAPID_EVENT_THRESHOLD - timeSinceLastScroll;
    scrollTimeoutId = setTimeout(() => {
      executeScroll();
      scrollTimeoutId = null;
    }, delayBeforeScroll);
  }
};

const onUIElementFocus = ({ y }: { y: number }) => {
  animatedScroll(y - 40);
};

const cancelAnimatedScroll = () => {
  if (scrollTimeoutId) {
    clearTimeout(scrollTimeoutId);
    scrollTimeoutId = null;
  }
  if (animationFrameId !== null) {
    cancelAnimationFrame(animationFrameId);
    animationFrameId = null;
  }
};

export { onUIElementFocus, animatedScroll, cancelAnimatedScroll };
